import React, {useCallback, useEffect} from "react";
import {useSubscription} from "../../subscription/useSubscription";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components";
import {useHistory, useParams} from "react-router";
import {Rankings} from "./ranking/Rankings";
import {IdeasForPortfolio} from "./ideas/IdeasForPortfolio";
import ideaActionsFactory from "../../idea/ideaActions";
import IdeaDetailsModal from "../../idea/modal/IdeaDetailsModal";
import PortfolioAuthorHeader from "./header/PortfolioAuthorHeader";
import portfolioAuthorActionsFactory from "./portfolioAuthorActions";
import {authorBackgroundColour, tableBorderColour} from "../../../style/styleConsts";
import {PortfolioRequirements} from "./PortfolioRequirements";
import {checkPortfolioAuthorForUpdate} from "./checkPortfolioAuthorForUpdate";
import {CentredLoader} from "../../elements/Loader";
import {logAction} from "../../../logging/logEvent";

const Page = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    min-height: 400px;
    background-color: ${authorBackgroundColour};
    height: 100%;
    overflow-x: hidden;
`;

const PerformanceLists = styled.div`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    align-items: stretch;
    justify-content: stretch;
    background-color: ${authorBackgroundColour};   
`;

const IdeasContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 568px;
    padding-left: 18px;    
    padding-top: 10px; 
    padding-right: 18px;
    padding-bottom: 12px;
    background-color: ${props => props.lookingAtSomeoneElse ? "#f0f4eb" : "#fcfaf8"}
`;

const VerticalSeperator = styled.div`
    flex-grow: 0;
    border-left: solid 2px ${tableBorderColour};
`;

const RankingsAndRequirementsContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    margin-bottom: 12px;
    margin-right: 18px;
    padding-left: 18px;    
    padding-top: 10px;
    flex-basis: 568px;
`;

const RequirementsContainer = styled.div`
    margin-top: 14px;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 252px;
    overflow-y: auto;
    max-height: fit-content;
`;

const PortfolioAuthor = ({}) => {
    const {authorId, portfolioId, rewardPeriodBasis = null, rewardPeriodStartDate = null} = useParams();
    const history = useHistory();
    useSubscription("PORTFOLIO_AUTHOR", `/ui/api/portfolioAuthor/${portfolioId}/${authorId}${rewardPeriodBasis !== null && rewardPeriodStartDate !== null ? `/period/${rewardPeriodBasis}/${rewardPeriodStartDate}` : ""}`, 60);
    const {viewerId, portfolioAuthor, loading, ideasView, rankingsView, openIdeasSort, closedIdeasSort, previousIdeasSort, rankingsSort, scrollToAuthor} =
        useSelector(({userIdentity: {imeUserId: viewerId}, portfolioAuthor: {loading, content, ideasView, rankingsView, openIdeasSort, previousIdeasSort, closedIdeasSort, rankingsSort, scrollToAuthor}}) =>
            ({
                viewerId,
                portfolioAuthor: content,
                loading,
                ideasView,
                rankingsView,
                openIdeasSort,
                closedIdeasSort,
                previousIdeasSort,
                rankingsSort,
                scrollToAuthor
            }));
    const dispatch = useDispatch();
    const ideaActions = ideaActionsFactory(dispatch);
    const portfolioAuthorActions = portfolioAuthorActionsFactory(dispatch);
    useEffect(() => {
        return () => {
            portfolioAuthorActions.reset();
        };
    }, []);

    const onRankingRowClick = useCallback(clickedAuthorId => {
        portfolioAuthorActions.selectAuthor();
        logAction({
            type: "PORTFOLIO_AUTHOR_RANKING_ROW_CLICKED",
            viewerId,
            authorId: clickedAuthorId,
            portfolioId
        });

        let url;
        if (rewardPeriodBasis !== null) {
            url = "/contribute/" + portfolioId + "/" + clickedAuthorId + "/period/" + rewardPeriodBasis + "/" + rewardPeriodStartDate;
        } else {
            url = "/contribute/" + portfolioId + "/" + clickedAuthorId;
        }
        history.push(url);
    }, [viewerId, portfolioId, rewardPeriodBasis, rewardPeriodStartDate]);

    if (loading) {
        return <div style={{padding: 20}}><CentredLoader/></div>;
    }

    const lookingAtSomeoneElse = viewerId !== authorId;
    const requirements = portfolioAuthor.openIdeas !== null ? (
        <RequirementsContainer>
            <PortfolioRequirements portfolioName={portfolioAuthor.portfolioName}
                                   portfolioCompanyId={portfolioAuthor.portfolioCompanyId}
                                   portfolioCompanyName={portfolioAuthor.portfolioCompanyName}
                                   authorCompanyId={portfolioAuthor.authorCompanyId}
                                   description={portfolioAuthor.portfolioDescription}
                                   openIdeaCount={portfolioAuthor.openIdeas.length}
                                   currentInvestment={{
                                       overall: portfolioAuthor.currentInvestment,
                                       ...portfolioAuthor.regionalInvestment
                                   }}
                                   kpi={portfolioAuthor.kpi}
                                   performanceSettings={portfolioAuthor.performanceSettings}
                                   requirements={portfolioAuthor.portfolioRequirements}
                                   showStatus={!lookingAtSomeoneElse}
                                   authorId={authorId}/>
        </RequirementsContainer>
    ) : null;

    const hasPercentReturnAutoCloseTrigger = portfolioAuthor.portfolioRequirements &&
                                             portfolioAuthor.portfolioRequirements.autoCloseTriggers &&
                                             portfolioAuthor.portfolioRequirements.autoCloseTriggers.flatMap(t => t.triggers)
                                                                                                    .filter(t => t.byPerformance && t.byPerformance.kpi === "Portfolio")
                                                                                                    .length > 0;
    return (
        <Page className="t-portfolio-author-page">
            <PortfolioAuthorHeader portfolioAuthor={portfolioAuthor} portfolioAuthorActions={portfolioAuthorActions} authorId={viewerId}/>
            <PerformanceLists>
                <RankingsAndRequirementsContainer>
                    <Rankings portfolioAuthorActions={portfolioAuthorActions}
                              portfolioAuthor={portfolioAuthor}
                              selectedAuthorId={authorId}
                              signedOnUserId={viewerId}
                              sort={rankingsSort}
                              rankingsView={rankingsView}
                              onRowClick={onRankingRowClick}
                              scrollToAuthor={scrollToAuthor}/>
                    {requirements}
                </RankingsAndRequirementsContainer>
                <VerticalSeperator/>
                <IdeasContainer className="t-ideas-container" lookingAtSomeoneElse={lookingAtSomeoneElse}>
                    <IdeasForPortfolio portfolioAuthor={portfolioAuthor}
                                       selectedAuthorId={authorId}
                                       ideaActions={ideaActions}
                                       ideasView={ideasView}
                                       lookingAtSomeoneElse={lookingAtSomeoneElse}
                                       openIdeasSort={openIdeasSort}
                                       closedIdeasSort={closedIdeasSort}
                                       previousIdeasSort={previousIdeasSort}
                                       portfolioAuthorActions={portfolioAuthorActions}
                                       hasPercentReturnAutoCloseTrigger={hasPercentReturnAutoCloseTrigger}/>
                </IdeasContainer>
            </PerformanceLists>
            <IdeaDetailsModal checkIdeaClosed={checkPortfolioAuthorForUpdate}/>
        </Page>
    );
};

export default PortfolioAuthor;
